import { WatchOptions } from 'vue'

import { Channel, ChatRoom, JobOffer, JobOfferResponse, MesgFilterInfo, MessageSummary, Profile, User_ChatRoomView } from '@dinosband/bv-main-schema/dist/types'
import { ChatRoomLastMessageInfo, Message } from '@dinosband/bv-messenger-schema'
import { PubSub, _last } from '@dinosband/dbi-utils'

import { CHAT_ROOM_UNREAD_COUNT } from '@dinosband/bv-data-center'
import { ChatRoomLastRead, ChatRoomModule, ChatRoomState, MessengerState, MessengerVuex } from '../vuex'

export class ChatRoomStore {

  chatRoomId: number
  vuex: MessengerVuex
  pubsub: PubSub
  modulePath: string
  state!: ChatRoomState

  constructor (chatRoomId: number, vuex: MessengerVuex, pubsub: PubSub) {
    this.chatRoomId = chatRoomId
    this.vuex = vuex
    this.pubsub = pubsub
    this.modulePath = `chatRoom:${this.chatRoomId}`
    this.state = this.initStoreModule()
  }

  init (chatRoom: ChatRoom) {
    this.commit('setChatRoom', chatRoom)
    if (chatRoom.props) {
      this.commit('setProps', JSON.parse(chatRoom.props))
    }
    this.updateUnreadCount()
  }

  cleanup () {
    this.cleanupStoreModule()
  }

  get chatRoom () { return this.state.chatRoom }

  get props () { return this.state.props }

  get channel () { return this.state.channel }
  set channel (channel: Channel | null | undefined) {
    this.commit('setChannel', channel)
  }

  get userProfile () { return this.state.userProfile }
  set userProfile (userProfile: Profile | undefined) {
    this.commit('setUserProfile', userProfile)
  }

  get userView () { return this.state.userView }
  set userView (userView: User_ChatRoomView | undefined) {
    this.commit('setUserView', userView)
    this.updateUnreadCount()
  }

  get rootMessage () { return this.state.rootMessage }
  set rootMessage (message: Message | undefined) {
    this.commit('setRootMessage', message)
  }

  get rootChatRoom () { return this.state.rootChatRoom }
  set rootChatRoom (chatRoom: ChatRoom | undefined) {
    this.commit('setRootChatRoom', chatRoom)
  }

  get firstMessageId () { return this.state.firstMessageId }
  set firstMessageId (messageId: number | undefined) {
    this.commit('setFirstMessageId', messageId)
  }

  get lastRead () { return this.state.lastRead }
  set lastRead (lastRead: ChatRoomLastRead | undefined) {
    this.commit('setLastRead', lastRead)
    this.updateUnreadCount()
  }

  get lastMessage () { return this.state.lastMessage }
  set lastMessage (message: Message | undefined) {
    this.commit('setLastMessage', message)
    this.updateUnreadCount()
  }

  get jobOffer () { return this.state.jobOffer }
  set jobOffer (jobOffer: JobOffer | undefined) {
    this.commit('setJobOffer', jobOffer)
  }

  get jobOfferResponse () { return this.state.jobOfferResponse }
  set jobOfferResponse (jsrp: JobOfferResponse | undefined) {
    this.commit('setJobOfferResponse', jsrp)
  }

  get messages () { return this.state.messages }
  set messages (messages: Message[]) {
    this.replaceMessages(messages)
  }

  get unreadCount () { return this.state.unreadCount }

  get isMyJobOffer () { return this.state.isMyJobOffer }
  set isMyJobOffer (value: boolean) {
    this.commit('setIsMyJobOffer', value)
  }

  get filterInfo () { return this.state.filterInfo }
  set filterInfo (filterInfo: MesgFilterInfo | undefined) {
    this.commit('setFilterInfo', filterInfo)
  }

  // get filterEnabled () { return this.state.filterEnabled }
  // set filterEnabled (value: boolean | undefined) {
  //   this.commit('setFilterEnabled', value)
  // }

  get profiles () { return this.state.profiles }
  set profiles (profiles: Profile[] | undefined) {
    this.commit('setProfiles', profiles ?? [])
  }

  get pushNotificationId () { return this.state.pushNotificationId }
  set pushNotificationId (notificationId: number | null) {
    this.commit('setPushNotificationId', notificationId)
  }

  addMessage (message: Message | undefined) {
    if (!message) {
      return
    }
    const messages = [...this.state.messages]
    const exist = messages.findIndex((i) => i.id == message.id)
    if (0 <= exist) {
      messages[exist] = { ...messages[exist], ...message }
    } else {
      messages.push(message)
    }
    this.replaceMessages(messages)
  }

  removeMessage (message: Message | undefined) {
    if (!message || !this.state.messages.some((i) => i.id === message.id)) {
      return
    }
    const messages = this.state.messages.filter((i) => i.id !== message.id)
    this.replaceMessages(messages)
  }

  updateMessage (message: Message | undefined, valid: boolean) {
    if (!message) {
      return
    }
    const messages = this.state.messages
    if (messages.length === 0) {
      // 아직 아무것도 없으므로 무시한다. BEEZ-1635
      return
    }
    const idx = messages.findIndex((i) => i.id === message.id)
    if (0 <= idx) {
      messages[idx] = { ...messages[idx], ...message }
    } else if (valid) {
      const i = messages.findIndex((i) => message.id < i.id)
      if (i == 0) { // 0은 동떨어진 것일 수 있으므로 버린다.
        return
      }
      if (0 < i) {
        messages.splice(i, 0, message)
      } else {
        messages.push(message)
      }
    } else {
      return
    }
    this.replaceMessages(messages)
  }

  get messageSummaries () { return this.state.messageSummaries }

  addMessageSummaries (summaries: MessageSummary[]) {
    this.commit('addMessageSummaries', summaries)
  }

  clearMessageSummaries () {
    this.commit('clearMessageSummaries', undefined)
  }

  get commentsChatRoomInfos () { return this.state.commentsChatRoomInfos }

  addCommentsChatRoomInfos (chatRoomInfos: ChatRoomLastMessageInfo[]) {
    this.commit('addCommentsChatRoomInfos', chatRoomInfos)
  }

  clearCommentsChatRoomInfos () {
    this.commit('clearCommentsChatRoomInfos', undefined)
  }

  updateUserReadMessageId (userView: User_ChatRoomView) {
    this.commit('updateUserReadMessageId', userView)
  }

  watch<T> (getter: (state: MessengerState) => T, cb: (value: T, oldValue: T) => void, options?: WatchOptions) {
    return this.vuex.watch(getter, cb, options)
  }

  private initStoreModule () {
    if (this.vuex.hasModule(this.modulePath)) {
      console.error('already registered', this.modulePath)
    } else {
      this.vuex.registerModule(this.modulePath, ChatRoomModule)
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.vuex.state as any)[this.modulePath] as ChatRoomState
  }

  private cleanupStoreModule () {
    this.vuex.unregisterModule(this.modulePath)
  }

  private commit (path: string, value: unknown) {
    this.vuex.commit(`${this.modulePath}/${path}`, value)
  }

  private replaceMessages (messages: Message[]) {
    this.commit('setMessages', messages)
    const lastMessage = _last(messages)
    if (lastMessage) {
      this.commit('setLastMessage', lastMessage)
      this.updateUnreadCount()
    }
  }

  private updateUnreadCount () {
    let count = 0
    if (this.state.lastMessage) {
      if (this.state.lastRead) {
        count = this.state.lastMessage.seq - this.state.lastRead.seq
      } else {
        count = this.state.lastMessage.seq
        if (this.state.chatRoom?.rootMessageId || this.state.chatRoom?.commentsRootMessageId) {
          count -= 1
        }
      }
    }
    this.commit('setUnreadCount', 0 < count ? count : 0)
    if (this.state.chatRoom) {
      this.pubsub.publish(CHAT_ROOM_UNREAD_COUNT, this)
    }
  }

  updateUserProfile (userProfiles: Profile[]) {
    if (!userProfiles.length) {
      return
    }
    const participantIds = this.profiles?.map((i) => i.id)
    if (!participantIds?.length) {
      return
    }
    const userProfile = userProfiles.find((i) => participantIds.includes(i.id))
    this.userProfile = userProfile
  }

}